import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { motion } from 'framer-motion'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import HeroVideo from '../components/HeroVideo'
import { formatDateLocal, formatTime } from '../utils/dateUtils'
import { 
  CalendarIcon, 
  MapPinIcon, 
  UserGroupIcon, 
  ClockIcon,
  CheckCircleIcon,
  XCircleIcon,
  QuestionMarkCircleIcon,
  PlusIcon
} from '@heroicons/react/24/outline'
import { useAuth0 } from '@auth0/auth0-react'
import { useLocationsApi, Location as ApiLocation } from '../services/locationsApi'
import { useEventsApi, Event as ApiEvent } from '../services/eventsApi'
import { isAdmin } from '../utils/roles'
import CreateEventModal from '../components/CreateEventModal'
import LoadingSpinner from '../components/LoadingSpinner'
import toast from 'react-hot-toast'

interface Location {
  id: string
  name: string
  address: string
  description?: string
}

type RSVPStatus = 'yes' | 'no' | 'maybe' | null

export default function EventsPage() {
  const { user } = useAuth0()
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const [showCreateModal, setShowCreateModal] = useState(false)
  const eventsApi = useEventsApi()
  const locationsApi = useLocationsApi()

  // TanStack Query for events
  const { 
    data: eventsData, 
    isLoading: eventsLoading, 
    error: eventsError 
  } = useQuery({
    queryKey: ['events'],
    queryFn: () => eventsApi.getEvents(),
    staleTime: 1000 * 60 * 5, // 5 minutes
    onError: (error) => {
      toast.error('Failed to load events')
    }
  })

  // TanStack Query for locations
  const { 
    data: locationsData, 
    isLoading: locationsLoading, 
    error: locationsError 
  } = useQuery({
    queryKey: ['locations'],
    queryFn: () => locationsApi.getLocations(),
    staleTime: 1000 * 60 * 10, // 10 minutes
    onError: (error) => {
      toast.error('Failed to load locations')
    }
  })

  // Transform data
  const events = eventsData?.events || []
  const locations: Location[] = locationsData?.locations.map((loc: ApiLocation) => ({
    id: loc.locationId,
    name: loc.name,
    address: `${loc.address}${loc.city ? ', ' + loc.city : ''}${loc.state ? ', ' + loc.state : ''}`,
    description: loc.description
  })) || []

  const loading = eventsLoading || locationsLoading

  // Mutation for RSVP
  const rsvpMutation = useMutation({
    mutationFn: ({ eventId, status, days }: { eventId: string, status: RSVPStatus, days?: string[] }) => 
      eventsApi.rsvpToEvent(eventId, status, days),
    onSuccess: () => {
      // Invalidate and refetch events data
      queryClient.invalidateQueries({ queryKey: ['events'] })
      toast.success('RSVP updated successfully!')
    },
    onError: (error) => {
      toast.error('Failed to update RSVP')
    }
  })

  // Mutation for creating events
  const createEventMutation = useMutation({
    mutationFn: (eventData: {
      title: string
      description: string
      eventDate: string
      time: string
      locationId: string
      maxParticipants?: number
      rsvpDeadline?: string
    }) => eventsApi.createEvent({
      ...eventData,
      status: 'active' // Default to active status
    }),
    onSuccess: () => {
      // Invalidate and refetch events data
      queryClient.invalidateQueries({ queryKey: ['events'] })
      setShowCreateModal(false)
      toast.success('Event created successfully!')
    },
    onError: (error) => {
      toast.error('Failed to create event')
    }
  })

  const handleRSVP = (eventId: string, status: RSVPStatus, days?: string[]) => {
    if (!user) {
      toast.error('Please log in to RSVP')
      return
    }
    rsvpMutation.mutate({ eventId, status, days })
  }

  const handleCreateEvent = (eventData: {
    title: string
    description: string
    eventDate: string
    time: string
    locationId: string
    maxParticipants?: number
    rsvpDeadline?: string
  }) => {
    if (!user) {
      toast.error('Please log in to create events')
      return
    }
    createEventMutation.mutate(eventData)
  }

  const getUserRSVP = (event: ApiEvent): RSVPStatus => {
    if (!user || !event.rsvps) return null
    const userRSVP = event.rsvps.find(rsvp => rsvp.userId === user.id)
    return userRSVP ? userRSVP.status : null
  }


  const getLocationName = (locationId: string) => {
    const location = locations.find(loc => loc.id === locationId)
    return location ? location.name : 'Unknown Location'
  }

  const getLocationAddress = (locationId: string) => {
    const location = locations.find(loc => loc.id === locationId)
    return location ? location.address : ''
  }

  const getRSVPStats = (event: ApiEvent | any) => {
    // Prefer precomputed counts from backend if available
    if ((event as any).rsvpCounts) return (event as any).rsvpCounts
    if (!event.rsvps) return { yes: 0, no: 0, maybe: 0 }
    const yes = event.rsvps.filter((rsvp: any) => rsvp.status === 'yes').length
    const no = event.rsvps.filter((rsvp: any) => rsvp.status === 'no').length
    const maybe = event.rsvps.filter((rsvp: any) => rsvp.status === 'maybe').length
    return { yes, no, maybe }
  }

  // Show loading spinner while data is loading
  if (loading) {
    return (
      <div className="min-h-screen bg-rb-dark">
        <HeroVideo 
          title="Midwest Railbirds Events"
          subtitle="Join us for unforgettable racing experiences"
          height="md"
        />
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <div className="flex justify-center items-center h-64">
            <LoadingSpinner />
          </div>
        </div>
      </div>
    )
  }

  // Show error state if both queries failed
  if (eventsError && locationsError) {
    return (
      <div className="min-h-screen bg-rb-dark">
        <HeroVideo 
          title="Midwest Railbirds Events"
          subtitle="Join us for unforgettable racing experiences"
          height="md"
        />
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <div className="text-center py-12">
            <h2 className="text-2xl font-bold text-white mb-4">Unable to load events</h2>
            <p className="text-white/80 mb-6">Please check your connection and try again.</p>
            <button 
              onClick={() => {
                queryClient.invalidateQueries({ queryKey: ['events'] })
                queryClient.invalidateQueries({ queryKey: ['locations'] })
              }}
              className="bg-primary-600 hover:bg-primary-700 text-white px-6 py-2 rounded-md transition-colors"
            >
              Retry
            </button>
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-rb-dark">
      <HeroVideo 
        title="Midwest Railbirds Events"
        subtitle="Join us for unforgettable racing experiences"
        height="md"
      />
      
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header with Create Event Button */}
        <div className="flex justify-between items-center mb-8">
          <div>
            <h1 className="text-3xl font-bold text-white mb-2">Upcoming Events</h1>
            <p className="text-gray-400">
              {events.length} event{events.length !== 1 ? 's' : ''} scheduled
            </p>
          </div>
          {isAdmin(user) && (
            <button
              onClick={() => setShowCreateModal(true)}
              className="bg-primary-600 hover:bg-primary-700 text-white px-4 py-2 rounded-md flex items-center gap-2 transition-colors"
            >
              <PlusIcon className="w-5 h-5" />
              Create Event
            </button>
          )}
        </div>

        {/* Events List */}
        {events.length === 0 ? (
          <div className="text-center py-12">
            <CalendarIcon className="w-16 h-16 text-white/60 mx-auto mb-4" />
            <h2 className="text-xl font-semibold text-white mb-2">No events scheduled</h2>
            <p className="text-white/80">Check back later for upcoming events.</p>
          </div>
        ) : (
          <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
            {events.map((event) => {
              const userRSVP = getUserRSVP(event)
              const rsvpStats = getRSVPStats(event)
              
              return (
                <motion.div
                  key={event.eventId}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  className="bg-white dark:bg-gray-800 rounded-lg overflow-hidden shadow-lg hover:shadow-xl transition-shadow cursor-pointer border border-gray-200 dark:border-gray-700"
                  onClick={() => navigate(`/events/${event.eventId}`)}
                >
                  <div className="p-6">
                    <div className="flex justify-between items-start mb-4">
                      <h3 className="text-xl font-semibold text-gray-900 dark:text-white truncate flex-1 mr-2">
                        {event.title}
                      </h3>
                      <span className={`px-2 py-1 text-xs font-medium rounded-full ${
                        event.status === 'upcoming' ? 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-300' :
                        event.status === 'ongoing' ? 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-300' :
                        'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-300'
                      }`}>
                        {event.status}
                      </span>
                    </div>
                    
                    <p className="text-gray-600 dark:text-gray-400 text-sm mb-4 line-clamp-2">
                      {event.description}
                    </p>
                    
                    <div className="space-y-2 mb-4">
                      <div className="flex items-center text-sm text-gray-700 dark:text-gray-300">
                        <CalendarIcon className="w-4 h-4 mr-2" />
                        {formatDateLocal(event.startDate)}
                        {event.endDate && event.endDate !== event.startDate && (
                          <span> - {formatDateLocal(event.endDate)}</span>
                        )}
                      </div>
                      
                      <div className="flex items-center text-sm text-gray-700 dark:text-gray-300">
                        <ClockIcon className="w-4 h-4 mr-2" />
                        {formatTime(event.time)}
                      </div>
                      
                      <div className="flex items-center text-sm text-gray-700 dark:text-gray-300">
                        <MapPinIcon className="w-4 h-4 mr-2" />
                        <div>
                          <div>{getLocationName(event.locationId)}</div>
                          <div className="text-xs text-gray-500 dark:text-gray-500">{getLocationAddress(event.locationId)}</div>
                        </div>
                      </div>
                      
                      <div className="flex items-center text-sm text-gray-700 dark:text-gray-300">
                        <UserGroupIcon className="w-4 h-4 mr-2" />
                        {rsvpStats.yes} attending • {rsvpStats.maybe} maybe • {rsvpStats.no} not attending
                      </div>
                    </div>
                    
                    {/* RSVP Section removed per request on list view */}
                  </div>
                </motion.div>
              )
            })}
          </div>
        )}
      </div>

      {/* Create Event Modal */}
      {showCreateModal && (
        <CreateEventModal
          isOpen={showCreateModal}
          onClose={() => setShowCreateModal(false)}
          onSubmit={handleCreateEvent}
          locations={locations}
          isLoading={createEventMutation.isLoading}
        />
      )}
    </div>
  )
}
